import { pdf } from "@react-pdf/renderer";
import { useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { setNotification } from "features/Notification/notificationSlice";
import authHeader from "services/auth-header";
import API from "services/axios";
import { QuotePdf } from "./QuotePdf";
import QuotePdfButton from "./QuotePdfButton";

/**
 * 3-dots menu for the Quote details header.
 *  - View PDF     -> opens the options modal; on confirm builds the blob and
 *                    opens it in a new tab
 *  - Download PDF -> opens the options modal; on confirm builds the blob and
 *                    downloads as Q-<quoteId>.pdf
 *  - Send Quote   -> opens the existing Quote PDF/email modal (QuotePdfButton)
 *
 * Both View and Download go through the same "Select Pdf Options" modal
 * (Show Total / Show Qty) so no PDF is generated off stale option state.
 */
export default function QuotePdfMenu({ selectedQuote, disabled = false }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [mode, setMode] = useState("view"); // "view" | "download"
  const [pdfOptions, setPdfOptions] = useState({
    show_Total: true,
    show_qty: true,
    show_tax_rate: true,
  });

  // `disabled` is passed by the parent while the next quote is still loading,
  // so the menu can't act on a stale/half-loaded quote. `loading` is our own
  // in-progress PDF build.
  const isBlocked = disabled || loading;

  const notify = (message, type = "error") =>
    dispatch(setNotification({ message, type }));

  const hasProducts = (selectedQuote?.quoteProducts?.length || 0) > 0;

  const openPdfModal = (nextMode) => {
    setMode(nextMode);
    setPdfOptions({ show_Total: true, show_qty: true, show_tax_rate: true });
    setShowPdfModal(true);
  };

  const closePdfModal = () => setShowPdfModal(false);

  const buildBlob = async () => {
    const { data: locationLogo } = await API.post(
      "/design-request/get-image",
      { path: selectedQuote?.location?.pdfLogo },
      { headers: authHeader() },
    );

    return pdf(
      <QuotePdf
        selectedQuote={selectedQuote}
        locationLogo={locationLogo}
        showTotal={pdfOptions.show_Total}
        showQty={pdfOptions.show_qty}
        showTaxRate={pdfOptions.show_tax_rate}
      />,
    ).toBlob();
  };

  const proceed = async () => {
    if (!hasProducts) return notify("This quote has no products.");

    // Pre-open a blank tab synchronously (this click IS a user gesture) so the
    // popup blocker doesn't kill the view tab after the async blob build.
    const viewWindow = mode === "view" ? window.open("", "_blank") : null;

    try {
      setLoading(true);
      const blob = await buildBlob();
      const url = URL.createObjectURL(blob);

      if (mode === "view") {
        if (viewWindow) viewWindow.location = url;
        else window.open(url, "_blank", "noopener,noreferrer");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `Q-${selectedQuote?.quoteId || "download"}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }

      // Revoke a bit later so the new tab / download can grab it first.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      closePdfModal();
    } catch (err) {
      if (viewWindow) viewWindow.close();
      notify(err?.message || "Failed to generate PDF");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle
          variant="primary"
          className="no-icon px-1"
          id="quote-pdf-menu"
          disabled={isBlocked}
        >
          {loading ? (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
          ) : (
            <i className="mdi mdi-dots-vertical"></i>
          )}
        </Dropdown.Toggle>

        <Dropdown.Menu style={{ width: 170 }}>
          <Dropdown.Item
            as="button"
            className="ps-2"
            onClick={() => openPdfModal("view")}
            disabled={isBlocked}
          >
            <i className="mdi mdi-eye h5 me-2"></i> View PDF
          </Dropdown.Item>
          <Dropdown.Item
            as="button"
            className="ps-2"
            onClick={() => openPdfModal("download")}
            disabled={isBlocked}
          >
            <i className="mdi mdi-download h4 me-1"></i> Download PDF
          </Dropdown.Item>
          <Dropdown.Item as="div" className="p-0 m-0">
            <QuotePdfButton
              btnClasses={"btn w-100 text-start text-nowrap"}
              btnTitle={
                <>
                  <i className="mdi mdi-email me-1"></i> Send Quote
                </>
              }
              selectedQuote={selectedQuote}
            />
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      <Modal
        show={showPdfModal}
        onHide={closePdfModal}
        contentClassName="bg-transparent"
        style={{ zIndex: 100002 }}
      >
        <Modal.Body className="custom-border-radius-sm bg-white shadow-lg px-3 py-2">
          <h3>Select Pdf Options</h3>
          <hr className="mb-3 mt-1" />
          <div className="my-3">
            {Object.keys(pdfOptions).map((option) => (
              <div key={option} className="d-flex gap-2 mb-1">
                <input
                  onChange={(e) =>
                    setPdfOptions((prev) => ({
                      ...prev,
                      [option]: e.target.checked,
                    }))
                  }
                  type="checkbox"
                  checked={pdfOptions[option]}
                  id={"quote-pdf-menu-modal-" + option}
                  data-switch="success"
                />
                <label
                  htmlFor={"quote-pdf-menu-modal-" + option}
                  data-on-label="Yes"
                  data-off-label="No"
                ></label>
                <div className="ms-2 fw-bold">
                  {option?.replaceAll("_", " ")?.toUpperCase()}
                </div>
              </div>
            ))}
          </div>
          {!hasProducts && (
            <div className="alert alert-warning py-2 px-3 mb-0 mt-3">
              <i className="mdi mdi-alert-outline me-1"></i>
              This quote has no products, so there is nothing to generate.
              Add at least one product to enable View / Download.
            </div>
          )}
          <div className="d-flex justify-content-end gap-1 mt-3">
            <button onClick={closePdfModal} className="btn btn-secondary">
              Close
            </button>
            <button
              disabled={loading || !hasProducts}
              className={`btn ${mode === "view" ? "btn-primary" : "btn-success"}`}
              onClick={proceed}
            >
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : mode === "view" ? (
                <>
                  <i className="mdi mdi-eye"></i> View
                </>
              ) : (
                <>
                  <i className="mdi mdi-download"></i> Download
                </>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
